import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { parseNumberInput, getNumberInputValue } from '../utils/numberInputHelpers';
import KeyboardAwareInput from './Shared/KeyboardAwareInput';
import KeyboardAwareNumberInput from './Shared/KeyboardAwareNumberInput';
import ProductFeaturedControls from './ProductFeaturedControls';

function ProductManagement({ theme }) {
    const { t, isSpanish } = useLanguage();
    const catName = (cat) => isSpanish ? (cat.name_español || cat.name) : cat.name;
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [optionGroups, setOptionGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showInactive, setShowInactive] = useState(false);
    const [filterCategory, setFilterCategory] = useState('');
    const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
    const [categoryFormData, setCategoryFormData] = useState({
        name: '',
        name_español: '',
        area: 'other',
        ordinal: ''
    });
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        description_español: '',
        category_id: '',
        cost: '',
        price: '',
        active: 1,
        option_groups: [],
        ordinal: ''
    });
    const [importing, setImporting] = useState(false);
    const [productSortField, setProductSortField] = useState('name');
    const [productSortDirection, setProductSortDirection] = useState('asc');
    const [searchTermProducts, setSearchTermProducts] = useState('');

    // View toggle and category management state
    const [activeView, setActiveView] = useState('products'); // 'products' or 'categories'
    const [searchTermCategories, setSearchTermCategories] = useState('');
    const [categorySortField, setCategorySortField] = useState('ordinal');
    const [categorySortDirection, setCategorySortDirection] = useState('asc');
    const [editingCategory, setEditingCategory] = useState(null);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState(new Set());
    const [showProductPicker, setShowProductPicker] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerSelectedIds, setPickerSelectedIds] = useState(new Set());
    const [moveTargetCategory, setMoveTargetCategory] = useState('');

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchOptionGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BACKEND_URL}/api/products`);
            if (!response.ok) throw new Error(t('error') + ': Failed to fetch products');
            const data = await response.json();
            setProducts(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/categories`);
            if (!response.ok) throw new Error(t('error') + ': Failed to fetch categories');
            const data = await response.json();
            setCategories(data);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
        }
    };
    const fetchOptionGroups = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/option_groups`);
            if (!response.ok) throw new Error(t('error') + ': Failed to fetch option groups');
            const data = await response.json();
            setOptionGroups(data);
        } catch (err) {
            console.error('Failed to fetch option groups:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const productData = {
                ...formData,
                restaurant_id: 1,
                cost: parseFloat(formData.cost) || 0,
                price: parseFloat(formData.price) || 0,
                ordinal: parseInt(formData.ordinal) || 1,
                option_groups: formData.option_groups || []
            };

            const url = editingProduct
                ? `${BACKEND_URL}/api/products/${editingProduct.id}`
                : `${BACKEND_URL}/api/products`;
            const method = editingProduct ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(productData)
            });

            if (!response.ok) throw new Error(t('error') + ': Failed to save product');

            await fetchProducts();
            resetForm();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        // Extract option group IDs from product.option_groups array
        const optionGroupIds = product.option_groups && Array.isArray(product.option_groups)
            ? product.option_groups.map(og => og.id)
            : [];
        setFormData({
            name: product.name || '',
            description: product.description || '',
            description_español: product.description_español || '',
            category_id: product.category_id || '',
            cost: product.cost || '',
            price: product.price || '',
            active: product.active,
            ordinal: product.ordinal || '',
            option_groups: optionGroupIds
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this product?')) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/products/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error(t('error') + ': Failed to delete product');
            await fetchProducts();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const toggleProductStatus = async (product) => {
        try {
            // Extract option group IDs from product.option_groups array
            const optionGroupIds = product.option_groups && Array.isArray(product.option_groups)
                ? product.option_groups.map(og => og.id)
                : [];

            const response = await fetch(`${BACKEND_URL}/api/products/${product.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: product.name,
                    description: product.description || '',
                    description_español: product.description_español || '',
                    category_id: product.category_id,
                    cost: product.cost || 0,
                    price: product.price,
                    active: product.active ? 0 : 1,
                    ordinal: product.ordinal || 1,
                    option_groups: optionGroupIds
                })
            });
            if (!response.ok) throw new Error('Failed to update product status');
            await fetchProducts();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleAddCategory = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/categories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...categoryFormData,
                    restaurant_id: 1,
                    ordinal: parseInt(categoryFormData.ordinal) || categories.length + 1
                })
            });

            if (!response.ok) throw new Error('Failed to add category');

            await fetchCategories();
            resetCategoryForm();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            description_español: '',
            category_id: '',
            cost: '',
            price: '',
            active: 1,
            ordinal: '',
            option_groups: [],
        });
        setEditingProduct(null);
        setShowAddForm(false);
    };

    const resetCategoryForm = () => {
        setCategoryFormData({
            name: '',
            name_español: '',
            area: 'other',
            ordinal: ''
        });
        setShowAddCategoryForm(false);
        setEditingCategory(null);
    };

    // Category sorting
    const handleCategorySort = (field) => {
        if (categorySortField === field) {
            setCategorySortDirection(categorySortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setCategorySortField(field);
            setCategorySortDirection('asc');
        }
    };

    // Expand/collapse a category row to show its products
    const toggleCategoryExpand = async (categoryId) => {
        if (expandedCategory === categoryId) {
            setExpandedCategory(null);
            setCategoryProducts([]);
            setSelectedProductIds(new Set());
            return;
        }
        setExpandedCategory(categoryId);
        setSelectedProductIds(new Set());
        try {
            const response = await fetch(`${BACKEND_URL}/api/categories/${categoryId}/products`);
            if (!response.ok) throw new Error('Failed to fetch');
            const data = await response.json();
            setCategoryProducts(data);
        } catch (err) {
            console.error('Failed to fetch category products:', err);
            setCategoryProducts([]);
        }
    };

    const toggleProductSelection = (productId) => {
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            if (next.has(productId)) next.delete(productId);
            else next.add(productId);
            return next;
        });
    };

    const handleMoveSelected = async () => {
        if (selectedProductIds.size === 0 || !moveTargetCategory) return;
        try {
            const response = await fetch(`${BACKEND_URL}/api/products/bulk-category`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productIds: [...selectedProductIds], categoryId: parseInt(moveTargetCategory) })
            });
            if (!response.ok) throw new Error('Failed to move products');
            setSelectedProductIds(new Set());
            setMoveTargetCategory('');
            fetchCategories();
            fetchProducts();
            toggleCategoryExpand(expandedCategory);
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleAddProductsToCategory = async (productIds) => {
        if (productIds.length === 0 || !expandedCategory) return;
        try {
            const response = await fetch(`${BACKEND_URL}/api/products/bulk-category`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productIds, categoryId: expandedCategory })
            });
            if (!response.ok) throw new Error('Failed to assign products');
            setShowProductPicker(false);
            setPickerSearch('');
            fetchCategories();
            fetchProducts();
            toggleCategoryExpand(expandedCategory);
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    // Edit category - populate form
    const handleEditCategory = (category) => {
        setEditingCategory(category);
        setCategoryFormData({
            name: category.name,
            name_español: category.name_español || '',
            area: category.area || 'other',
            ordinal: category.ordinal || ''
        });
        setShowAddCategoryForm(true);
    };

    // Save category (handles both create and update)
    const handleCategorySave = async (e) => {
        e.preventDefault();
        try {
            const url = editingCategory
                ? `${BACKEND_URL}/api/categories/${editingCategory.id}`
                : `${BACKEND_URL}/api/categories`;
            const method = editingCategory ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...categoryFormData,
                    restaurant_id: 1,
                    ordinal: parseInt(categoryFormData.ordinal) || categories.length + 1
                })
            });

            if (!response.ok) throw new Error('Failed to save category');

            await fetchCategories();
            resetCategoryForm();
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    // Delete category with safety check
    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm(t('confirmDelete'))) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/categories/${categoryId}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (!response.ok) {
                // Handle the specific error from backend when products are using the category
                if (response.status === 400 && data.productCount) {
                    alert(`${t('cannotDeleteCategory')}: ${data.productCount} ${t('productsUsingCategory')}`);
                } else if (response.status === 400 && data.customItemSales) {
                    alert(`${t('cannotDeleteCategory')}: ${t('customItemsUsingCategory')}`);
                } else {
                    throw new Error(data.message || 'Failed to delete category');
                }
                return;
            }

            await fetchCategories();
            alert(t('categoryDeleted') || 'Category deleted successfully');
        } catch (err) {
            alert(t('error') + ': ' + err.message);
        }
    };

    // Filter and sort categories
    const filteredAndSortedCategories = categories
        .filter(cat =>
            cat.name.toLowerCase().includes(searchTermCategories.toLowerCase())
        )
        .sort((a, b) => {
            const aVal = a[categorySortField];
            const bVal = b[categorySortField];
            const modifier = categorySortDirection === 'asc' ? 1 : -1;
            return aVal > bVal ? modifier : -modifier;
        });

    const formatCurrency = (amount) => {
        return `$${Number(amount).toFixed(2)}`;
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? catName(category) : 'Unknown';
    };

    const getOptionGroupName = (optionGroupsArray) => {
        // Handle product.option_groups array (new format from backend)
        if (Array.isArray(optionGroupsArray)) {
            if (optionGroupsArray.length === 0) return t('none') || 'None';
            return optionGroupsArray.map(g => g.name).filter(Boolean).join(', ');
        }
        return t('none') || 'None';
    };

    const handleSort = (field) => {
        if (productSortField === field) {
            setProductSortDirection(productSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setProductSortField(field);
            setProductSortDirection('asc');
        }
    };

    let filteredProducts = showInactive
        ? products
        : products.filter(product => product.active === 1 || product.active === true);

    // Apply category filter
    if (filterCategory) {
        filteredProducts = filteredProducts.filter(product =>
            product.category_id === parseInt(filterCategory)
        );
    }

    // Store count before search filter for display
    const productsBeforeSearch = filteredProducts.length;

    // Apply search filter
    if (searchTermProducts) {
        const search = searchTermProducts.toLowerCase();
        filteredProducts = filteredProducts.filter(product => {
            const categoryName = getCategoryName(product.category_id);
            return [
                product.name,
                product.description,
                product.description_español,
                categoryName,
                product.option_groups,
                product.price?.toString(),
                product.cost?.toString()
            ].some(field =>
                field && field.toString().toLowerCase().includes(search)
            );
        });
    }

    // Apply sorting
    filteredProducts = filteredProducts.sort((a, b) => {
        let aValue, bValue;

        switch (productSortField) {
            case 'name':
                aValue = (a.name || '').toLowerCase();
                bValue = (b.name || '').toLowerCase();
                break;
            case 'category':
                aValue = getCategoryName(a.category_id).toLowerCase();
                bValue = getCategoryName(b.category_id).toLowerCase();
                break;
            case 'cost':
                aValue = parseFloat(a.cost) || 0;
                bValue = parseFloat(b.cost) || 0;
                break;
            case 'price':
                aValue = parseFloat(a.price) || 0;
                bValue = parseFloat(b.price) || 0;
                break;
            case 'ordinal':
                aValue = parseInt(a.ordinal) || 0;
                bValue = parseInt(b.ordinal) || 0;
                break;
            case 'optionGroups':
                aValue = getOptionGroupName(a.option_groups).toLowerCase();
                bValue = getOptionGroupName(b.option_groups).toLowerCase();
                break;
            case 'id':
                aValue = parseInt(a.id) || 0;
                bValue = parseInt(b.id) || 0;
                break;
            default:
                aValue = a[productSortField];
                bValue = b[productSortField];
        }

        if (aValue < bValue) return productSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return productSortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    const exportHeaders = [
        t('name'),
        t('description'),
        t('descriptionSpanish'),
        t('category'),
        t('cost'),
        t('price'),
        t('active'),
        t('ordinal'),
        t('optionGroups'), // changed to plural and translated
        t('optionGroupName')
    ];

    const handleExport = () => {
        try {
            // Create CSV content
            const csvContent = [
                exportHeaders.join(','),
                ...filteredProducts.map(product => [
                    `"${product.name}"`,
                    `"${product.description || ''}"`,
                    `"${product.description_español || ''}"`,
                    `"${categories.find(cat => cat.id === product.category_id)?.name || ''}"`,
                    product.cost || '0',
                    product.price || '0',
                    product.active !== undefined ? product.active : '1',
                    product.ordinal || '0',
                    product.option_groups && product.option_groups.length > 0
                        ? product.option_groups.map(og => og.id).join(';')
                        : '',
                    `"${getOptionGroupName(product.option_groups) || ''}"`
                ].join(','))
            ].join('\n');

            // Create and download file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            alert('Error exporting products: ' + err.message);
        }
    };

    const handleExportDuplicates = (duplicateProducts) => {
        const csvContent = [
            exportHeaders.join(','),
            ...duplicateProducts.map(({ existing }) => [
                `"${existing.name}"`,
                `"${existing.description || ''}"`,
                `"${existing.description_español || ''}"`,
                `"${categories.find(cat => cat.id === existing.category_id)?.name || ''}"`,
                existing.cost || '0',
                existing.price || '0',
                existing.active !== undefined ? existing.active : '1',
                existing.ordinal || '0',
                existing.option_groups && existing.option_groups.length > 0
                    ? existing.option_groups.map(og => og.id).join(';')
                    : '',
                `"${getOptionGroupName(existing.option_groups) || ''}"`
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `existing_products_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setImporting(true);
        try {
            const text = await file.text();
            const lines = text.split('\n').filter(line => line.trim());

            // Parse CSV with proper handling of quoted values
            const parseCSVLine = (line) => {
                const result = [];
                let current = '';
                let inQuotes = false;

                for (let i = 0; i < line.length; i++) {
                    const char = line[i];
                    if (char === '"') {
                        inQuotes = !inQuotes;
                    } else if (char === ',' && !inQuotes) {
                        result.push(current.trim());
                        current = '';
                    } else {
                        current += char;
                    }
                }
                result.push(current.trim());
                return result.map(v => v.replace(/^"|"$/g, ''));
            };

            const headers = parseCSVLine(lines[0]);

            // Validate headers
            const expectedHeaders = ['Name', 'Description', 'Description Español', 'Category', 'Cost', 'Price', 'Active', 'Ordinal'];
            const hasValidHeaders = expectedHeaders.slice(0, 6).every(header =>
                headers.some(h => h.toLowerCase().includes(header.toLowerCase()))
            );

            if (!hasValidHeaders) {
                throw new Error('Invalid CSV format. Expected headers: Name, Description, Description Español, Category, Cost, Price');
            }

            const productsToImport = [];
            const duplicateProducts = [];

            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;

                const values = parseCSVLine(line);
                if (values.length >= 6) {
                    const productName = values[0];

                    // Check if product already exists (case-insensitive)
                    const existingProduct = products.find(p =>
                        p.name.toLowerCase() === productName.toLowerCase()
                    );

                    if (existingProduct) {
                        duplicateProducts.push({
                            name: productName,
                            existing: existingProduct
                        });
                        continue; // Skip this product
                    }

                    // Find category ID by name
                    const categoryName = values[3];
                    const category = categories.find(cat => cat.name.toLowerCase() === categoryName.toLowerCase());

                    // Parse option groups (values[8] contains semicolon-separated IDs)
                    const optionGroupIds = values[8] ? values[8].split(';').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [];

                    productsToImport.push({
                        restaurant_id: 1,
                        name: productName,
                        description: values[1] || '',
                        description_español: values[2] || '',
                        category_id: category ? category.id : (categories.length > 0 ? categories[0].id : 1),
                        cost: parseFloat(values[4]) || 0,
                        price: parseFloat(values[5]) || 0,
                        active: values[6] !== undefined ? parseInt(values[6]) || 1 : 1,
                        ordinal: values[7] !== undefined ? parseInt(values[7]) || 0 : 0,
                        option_groups: optionGroupIds
                    });
                }
            }

            // Import products one by one
            let importedCount = 0;
            const errorMessages = [];

            for (const product of productsToImport) {
                try {
                    const response = await fetch(`${BACKEND_URL}/api/products`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(product)
                    });

                    if (response.ok) {
                        importedCount++;
                    } else {
                        const errorData = await response.json();
                        errorMessages.push(`${product.name}: ${errorData.error || 'Unknown error'}`);
                        console.error('Error importing product:', product.name, errorData);
                    }
                } catch (err) {
                    errorMessages.push(`${product.name}: ${err.message}`);
                    console.error('Error importing product:', product.name, err);
                }
            }

            let message = `Successfully imported ${importedCount} out of ${productsToImport.length} products`;

            if (duplicateProducts.length > 0) {
                message += `\n\n${duplicateProducts.length} products already exist in the menu and were skipped:`;
                message += `\n${duplicateProducts.slice(0, 5).map(p => `• ${p.name}`).join('\n')}`;
                if (duplicateProducts.length > 5) {
                    message += `\n... and ${duplicateProducts.length - 5} more`;
                }
            }

            if (errorMessages.length > 0) {
                message += `\n\nErrors:\n${errorMessages.slice(0, 5).join('\n')}`;
                if (errorMessages.length > 5) {
                    message += `\n... and ${errorMessages.length - 5} more errors. Check console for details.`;
                }
            }

            alert(message);

            // If there were duplicates, ask if user wants to export existing products
            if (duplicateProducts.length > 0) {
                const exportDuplicates = window.confirm(
                    `Would you like to export the ${duplicateProducts.length} existing products that were skipped to a CSV file?`
                );
                if (exportDuplicates) {
                    handleExportDuplicates(duplicateProducts);
                }
            }
            await fetchProducts(); // Refresh the products list
        } catch (err) {
            alert('Error importing products: ' + err.message);
        } finally {
            setImporting(false);
            // Reset file input
            event.target.value = '';
        }
    };

    if (loading) return <div>Loading products...</div>;
    if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

    return (
        <div style={{
            background: theme.colors.surface,
            padding: '0rem',
            borderRadius: theme.borderRadius || 8,
            boxShadow: theme.boxShadow,
            marginBottom: '1.5rem'
        }}>
            {/* View Toggle */}
            <div style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: '1.5rem',
                borderBottom: `2px solid ${theme?.colors?.border || '#ddd'}`,
                paddingBottom: '0.5rem',
                paddingTop: '1.5rem',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem'
            }}>
                <button
                    onClick={() => setActiveView('products')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: activeView === 'products' ? theme?.colors?.primary || '#1976d2' : 'transparent',
                        color: activeView === 'products' ? theme?.colors?.surface || '#fff' : theme?.colors?.text || '#000',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 4,
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: activeView === 'products' ? 'bold' : 'normal'
                    }}
                >
                    {t('products')}
                </button>
                <button
                    onClick={() => setActiveView('categories')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        backgroundColor: activeView === 'categories' ? theme?.colors?.primary || '#1976d2' : 'transparent',
                        color: activeView === 'categories' ? theme?.colors?.surface || '#fff' : theme?.colors?.text || '#000',
                        border: 'none',
                        borderRadius: theme?.borderRadius || 4,
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: activeView === 'categories' ? 'bold' : 'normal'
                    }}
                >
                    {t('categories')}
                </button>
            </div>

            {/* Products View */}
            {activeView === 'products' && (
            <div>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                marginBottom: '1rem'
            }}>
                {/* Row 1: Filters */}
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: '1 1 auto', minWidth: '200px' }}>
                        <label style={{ fontSize: '0.9rem', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                            {t('filterByCategory')}:
                        </label>
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            style={{
                                padding: '0.4rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.primary}40`,
                                fontSize: '0.9rem',
                                backgroundColor: theme.colors.surface,
                                color: theme.colors.text,
                                flex: '1 1 auto'
                            }}
                        >
                            <option value="">{t('allCategories')}</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {catName(category)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <label style={{ fontSize: '0.9rem', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        {t('showInactiveProducts')}
                    </label>
                </div>
                {/* Row 2: Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleExport}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: theme.colors.success,
                            color: theme.colors.surface,
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            boxShadow: theme.boxShadow,
                            flex: '0 1 auto',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        📤 {t('export')}
                    </button>
                    <label style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: theme.colors.warning,
                        color: theme.colors.surface,
                        border: 'none',
                        borderRadius: theme.borderRadius || 8,
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.9rem',
                        boxShadow: theme.boxShadow,
                        display: 'flex',
                        alignItems: 'center',
                        flex: '0 1 auto',
                        whiteSpace: 'nowrap'
                    }}>
                        {importing ? `⏳ ${t('importing')}...` : `📥 ${t('import')}`}
                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleImport}
                            disabled={importing}
                            style={{ display: 'none' }}
                        />
                    </label>
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.surface,
                            border: 'none',
                            borderRadius: theme.borderRadius || 8,
                            cursor: 'pointer',
                            fontWeight: 'bold',
                            fontSize: '0.9rem',
                            boxShadow: theme.boxShadow,
                            flex: '0 1 auto',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {showAddForm ? t('cancel') : t('addProduct')}
                    </button>
                </div>
            </div>

            {showAddForm && (
                <form onSubmit={handleSubmit} style={{
                    background: theme.colors.background,
                    padding: '1rem',
                    borderRadius: theme.borderRadius || 8,
                    marginBottom: '1rem',
                    border: `1px solid ${theme.colors.primary}20`
                }}>
                    <h3 style={{ color: theme.colors.primary, margin: '0 0 1rem 0' }}>
                        {editingProduct ? 'Edit Product' : 'Add New Product'}
                    </h3>

                    {/* Top Button Section */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
                        <button
                            type="button"
                            onClick={resetForm}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme.colors.error || '#f44336',
                                color: theme.colors.surface,
                                border: 'none',
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme.colors.success || '#4caf50',
                                color: theme.colors.surface,
                                border: 'none',
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {editingProduct ? t('update') : t('add')} {t('product')}
                        </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('name')}:
                            </label>
                            <KeyboardAwareInput
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                style={{
                                    padding: '0.5rem',
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('category')}:
                            </label>
                            <select
                                value={formData.category_id}
                                onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                                required
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            >
                                <option value="">Select Category</option>
                                {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {catName(category)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('cost')}:
                            </label>
                            <KeyboardAwareNumberInput
                                value={formData.cost}
                                onChange={(value) => setFormData({...formData, cost: value})}
                                options={{ allowDecimals: true, min: 0 }}
                                placeholder="0.00"
                                style={{
                                    padding: '0.5rem',
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('price')}:
                            </label>
                            <KeyboardAwareNumberInput
                                value={formData.price}
                                onChange={(value) => setFormData({...formData, price: value})}
                                options={{ allowDecimals: true, min: 0 }}
                                placeholder="0.00"
                                required
                                style={{
                                    padding: '0.5rem',
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                Display Order:
                            </label>
                            <KeyboardAwareNumberInput
                                value={formData.ordinal}
                                onChange={(value) => setFormData({...formData, ordinal: value})}
                                options={{ min: 0 }}
                                placeholder="0"
                                style={{
                                    padding: '0.5rem',
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            />
                        </div>
                                                <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                    {t('optionGroups')} ({t('(Optional)')})
                            </label>
                            <select
                                multiple
                                value={formData.option_groups || []}
                                onChange={(e) => {
                                    const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                    setFormData({...formData, option_groups: selectedOptions});
                                }}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    minHeight: '120px',
                                        borderRadius: theme.borderRadius || 4,
                                        border: `1px solid ${theme.colors.primary}40`
                                    }}
                                >
                                    {optionGroups.map(group => (
                                        <option key={group.id} value={group.id}>
                                            {group.name}
                                        </option>
                                    ))}
                                </select>
                            <div style={{ fontSize: '0.85rem', marginTop: '0.25rem', color: theme?.colors?.textSecondary || '#666' }}>
                                {t('holdCtrlOrCmd')}
                            </div>
                        </div>

                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                {t('status')}:
                            </label>
                            <select
                                value={formData.active}
                                onChange={(e) => setFormData({...formData, active: parseInt(e.target.value)})}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: `1px solid ${theme.colors.primary}40`
                                }}
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            {t('description')} (English):
                        </label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            rows="2"
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.primary}40`,
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            {t('description')} (Español):
                        </label>
                        <textarea
                            value={formData.description_español}
                            onChange={(e) => setFormData({...formData, description_español: e.target.value})}
                            rows="2"
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                borderRadius: theme.borderRadius || 4,
                                border: `1px solid ${theme.colors.primary}40`,
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    {/* Bottom Button Section */}
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={resetForm}
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme.colors.error || '#f44336',
                                color: theme.colors.surface,
                                border: 'none',
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            type="submit"
                            style={{
                                padding: '0.75rem 1.5rem',
                                backgroundColor: theme.colors.success || '#4caf50',
                                color: theme.colors.surface,
                                border: 'none',
                                borderRadius: theme.borderRadius || 4,
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {editingProduct ? t('update') : t('add')} {t('product')}
                        </button>
                    </div>
                </form>
            )}

            {/* Search Input */}
            <div style={{ marginBottom: '1rem' }}>
                <KeyboardAwareInput
                    type="text"
                    value={searchTermProducts}
                    onChange={(e) => setSearchTermProducts(e.target.value)}
                    placeholder={t('searchProducts')}
                    onFocus={(e) => {
                        e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
                    }}
                    onBlur={(e) => {
                        e.target.style.borderColor = theme?.colors?.border || '#ddd';
                    }}
                    style={{
                        padding: '0.75rem',
                        fontSize: '0.95rem',
                        outline: 'none'
                    }}
                />
                {searchTermProducts && (
                    <div style={{
                        marginTop: '0.5rem',
                        fontSize: '0.85rem',
                        color: theme?.colors?.textSecondary || '#666'
                    }}>
                        {t('showing')} {filteredProducts.length} {t('of')} {productsBeforeSearch} {t('products').toLowerCase()}
                    </div>
                )}
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {filteredProducts.length === 0 ? (
                    <p style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
                        {t('noProductsFound')}
                    </p>
                ) : (
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '0.9rem'
                    }}>
                        <thead>
                            <tr style={{ borderBottom: `2px solid ${theme.colors.primary}` }}>
                                <th onClick={() => handleSort('name')} style={{ padding: '0.75rem', textAlign: 'left', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    {t('name')} {productSortField === 'name' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th onClick={() => handleSort('category')} style={{ padding: '0.75rem', textAlign: 'left', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    {t('category')} {productSortField === 'category' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th onClick={() => handleSort('cost')} style={{ padding: '0.75rem', textAlign: 'right', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    {t('cost')} {productSortField === 'cost' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th onClick={() => handleSort('price')} style={{ padding: '0.75rem', textAlign: 'right', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    {t('price')} {productSortField === 'price' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary }}>{t('status')}</th>
                                <th style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary }}>{t('photo')} / ★</th>
                                <th onClick={() => handleSort('ordinal')} style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    {t('displayOrder')} {productSortField === 'ordinal' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th onClick={() => handleSort('optionGroups')} style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    {t('optionGroups')} {productSortField === 'optionGroups' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th onClick={() => handleSort('id')} style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary, cursor: 'pointer', userSelect: 'none' }}>
                                    ID {productSortField === 'id' && (productSortDirection === 'asc' ? '▲' : '▼')}
                                </th>
                                <th style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.primary }}>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProducts.map(product => (
                                <tr key={product.id} style={{
                                    borderBottom: `1px solid ${theme.colors.primary}20`,
                                    opacity: product.active ? 1 : 0.6
                                }}>
                                    <td style={{ padding: '0.75rem' }}>
                                        <div style={{ fontWeight: 'bold' }}>{product.name}</div>

                                    </td>
                                    <td style={{ padding: '0.75rem' }}>{getCategoryName(product.category_id)}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(product.cost)}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                        {formatCurrency(product.price)}
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        <button
                                            onClick={() => toggleProductStatus(product)}
                                            style={{
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: product.active ? '#4caf50' : '#f44336',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: theme.borderRadius || 4,
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {product.active ? 'Active' : 'Inactive'}
                                        </button>
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        <ProductFeaturedControls product={product} onChanged={fetchProducts} />
                                    </td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>{product.ordinal}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>{getOptionGroupName(product.option_groups)}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center', color: theme.colors.textSecondary, fontSize: '0.85rem' }}>{product.id}</td>
                                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                        <button
                                            onClick={() => handleEdit(product)}
                                            style={{
                                                marginRight: '0.5rem',
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: theme.colors.primary,
                                                color: theme.colors.surface,
                                                border: 'none',
                                                borderRadius: theme.borderRadius || 4,
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {t('edit')}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(product.id)}
                                            style={{
                                                padding: '0.25rem 0.5rem',
                                                backgroundColor: '#f44336',
                                                color: theme.colors.surface,
                                                border: 'none',
                                                borderRadius: theme.borderRadius || 4,
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            {t('delete')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            </div>
            )}
            {/* End Products View */}

            {/* Categories View */}
            {activeView === 'categories' && (
                <div style={{ padding: '1.5rem' }}>
                    {/* Header with count and Add button */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, color: theme?.colors?.primary || '#1976d2' }}>
                            {t('categories')} ({categories.length})
                        </h3>
                        <button
                            onClick={() => setShowAddCategoryForm(true)}
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: theme?.colors?.primary || '#1976d2',
                                color: theme?.colors?.surface || '#fff',
                                border: 'none',
                                borderRadius: theme?.borderRadius || 4,
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            ➕ {t('addCategory')}
                        </button>
                    </div>

                    {/* Search bar for categories */}
                    <KeyboardAwareInput
                        type="text"
                        value={searchTermCategories}
                        onChange={(e) => setSearchTermCategories(e.target.value)}
                        placeholder={t('searchCategories')}
                        style={{
                            padding: '0.75rem',
                            marginBottom: '1rem',
                            fontSize: '1rem'
                        }}
                    />

                    {/* Categories table */}
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: theme?.colors?.surface || '#fff'
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                    color: theme?.colors?.surface || '#fff'
                                }}>
                                    <th style={{ padding: '0.75rem', width: '2rem' }}></th>
                                    <th onClick={() => handleCategorySort('name')} style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}>
                                        {t('name')} {categorySortField === 'name' && (categorySortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleCategorySort('area')} style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}>
                                        {t('area')} {categorySortField === 'area' && (categorySortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleCategorySort('active_product_count')} style={{
                                        padding: '0.75rem',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}>
                                        {t('products')} {categorySortField === 'active_product_count' && (categorySortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th onClick={() => handleCategorySort('ordinal')} style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}>
                                        {t('displayOrder')} {categorySortField === 'ordinal' && (categorySortDirection === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th style={{
                                        padding: '0.75rem',
                                        textAlign: 'left',
                                        fontWeight: 'bold'
                                    }}>
                                        {t('actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedCategories.map((category, index) => (
                                    <React.Fragment key={category.id}>
                                    <tr
                                        onClick={() => toggleCategoryExpand(category.id)}
                                        style={{
                                            backgroundColor: expandedCategory === category.id
                                                ? (theme?.colors?.primary || '#1976d2') + '18'
                                                : index % 2 === 0 ? theme?.colors?.surface : theme?.colors?.background,
                                            borderBottom: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                            cursor: 'pointer',
                                            transition: 'background-color 0.15s'
                                        }}
                                    >
                                        <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.8rem', color: theme?.colors?.textSecondary || '#666' }}>
                                            {expandedCategory === category.id ? '▼' : '▶'}
                                        </td>
                                        <td style={{ padding: '0.75rem', fontWeight: expandedCategory === category.id ? 'bold' : 'normal' }}>{catName(category)}</td>
                                        <td style={{ padding: '0.75rem' }}>{category.area}</td>
                                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                            <span style={{ fontWeight: 'bold', color: theme?.colors?.text || '#000' }}>
                                                {category.active_product_count || 0}
                                            </span>
                                            <span style={{ color: theme?.colors?.textSecondary || '#888', fontSize: '0.85rem' }}>
                                                {' / '}{category.total_product_count || 0}
                                            </span>
                                        </td>
                                        <td style={{ padding: '0.75rem' }}>{category.ordinal}</td>
                                        <td style={{ padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => handleEditCategory(category)}
                                                style={{
                                                    marginRight: '0.5rem',
                                                    padding: '0.25rem 0.5rem',
                                                    backgroundColor: theme?.colors?.primary || '#1976d2',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                ✏️ {t('edit')}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteCategory(category.id)}
                                                style={{
                                                    padding: '0.25rem 0.5rem',
                                                    backgroundColor: theme?.colors?.error || '#f44336',
                                                    color: theme?.colors?.surface || '#fff',
                                                    border: 'none',
                                                    borderRadius: theme?.borderRadius || 4,
                                                    cursor: 'pointer',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                🗑️ {t('delete')}
                                            </button>
                                        </td>
                                    </tr>
                                    {/* Expanded product list */}
                                    {expandedCategory === category.id && (
                                        <tr>
                                            <td colSpan="6" style={{
                                                padding: '1rem 1rem 1rem 2.5rem',
                                                backgroundColor: (theme?.colors?.background || '#f5f5f5'),
                                                borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`
                                            }}>
                                                {categoryProducts.length === 0 ? (
                                                    <p style={{ color: theme?.colors?.textSecondary || '#888', fontStyle: 'italic', margin: '0.5rem 0' }}>
                                                        {t('noProducts') || 'No products in this category'}
                                                    </p>
                                                ) : (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                                                        {categoryProducts.map(product => (
                                                            <label
                                                                key={product.id}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '0.4rem',
                                                                    padding: '0.4rem 0.75rem',
                                                                    backgroundColor: selectedProductIds.has(product.id)
                                                                        ? (theme?.colors?.primary || '#1976d2') + '22'
                                                                        : theme?.colors?.surface || '#fff',
                                                                    border: `1px solid ${selectedProductIds.has(product.id) ? theme?.colors?.primary || '#1976d2' : theme?.colors?.border || '#ddd'}`,
                                                                    borderRadius: theme?.borderRadius || 4,
                                                                    cursor: 'pointer',
                                                                    fontSize: '0.85rem',
                                                                    color: theme?.colors?.text || '#000',
                                                                    opacity: product.active ? 1 : 0.5
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedProductIds.has(product.id)}
                                                                    onChange={() => toggleProductSelection(product.id)}
                                                                />
                                                                {product.name}
                                                                {!product.active && <span style={{ fontSize: '0.7rem', color: theme?.colors?.textSecondary || '#999' }}>({t('inactive')})</span>}
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                    <button
                                                        onClick={() => { setShowProductPicker(true); setPickerSearch(''); setPickerSelectedIds(new Set()); }}
                                                        style={{
                                                            padding: '0.4rem 0.75rem',
                                                            backgroundColor: theme?.colors?.success || '#4caf50',
                                                            color: theme?.colors?.surface || '#fff',
                                                            border: 'none',
                                                            borderRadius: theme?.borderRadius || 4,
                                                            cursor: 'pointer',
                                                            fontSize: '0.85rem',
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        + {t('addProducts') || 'Add Products'}
                                                    </button>
                                                    {selectedProductIds.size > 0 && (
                                                        <>
                                                            <select
                                                                value={moveTargetCategory}
                                                                onChange={(e) => setMoveTargetCategory(e.target.value)}
                                                                style={{
                                                                    padding: '0.4rem',
                                                                    borderRadius: theme?.borderRadius || 4,
                                                                    border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                                                    backgroundColor: theme?.colors?.surface || '#fff',
                                                                    color: theme?.colors?.text || '#000',
                                                                    fontSize: '0.85rem'
                                                                }}
                                                            >
                                                                <option value="">{t('moveTo') || 'Move to...'}</option>
                                                                {categories.filter(c => c.id !== category.id).map(c => (
                                                                    <option key={c.id} value={c.id}>{catName(c)}</option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={handleMoveSelected}
                                                                disabled={!moveTargetCategory}
                                                                style={{
                                                                    padding: '0.4rem 0.75rem',
                                                                    backgroundColor: moveTargetCategory ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc'),
                                                                    color: theme?.colors?.surface || '#fff',
                                                                    border: 'none',
                                                                    borderRadius: theme?.borderRadius || 4,
                                                                    cursor: moveTargetCategory ? 'pointer' : 'not-allowed',
                                                                    fontSize: '0.85rem',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {t('moveSelected') || 'Move Selected'} ({selectedProductIds.size})
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {/* End Categories View */}

            {/* Product Picker Modal */}
            {showProductPicker && expandedCategory && (
                <div
                    onClick={() => { setShowProductPicker(false); setPickerSearch(''); }}
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: theme?.colors?.surface || '#fff',
                            borderRadius: theme?.borderRadius || 8,
                            padding: '1.5rem',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '80vh',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0, marginBottom: '1rem' }}>
                            {t('addProducts') || 'Add Products'} → {catName(categories.find(c => c.id === expandedCategory) || {})}
                        </h3>
                        <KeyboardAwareInput
                            type="text"
                            value={pickerSearch}
                            onChange={(e) => setPickerSearch(e.target.value)}
                            placeholder={t('search') || 'Search products...'}
                            style={{
                                padding: '0.75rem',
                                marginBottom: '0.75rem',
                                fontSize: '1rem'
                            }}
                        />
                        <div style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh' }}>
                            {(() => {
                                const available = products.filter(p =>
                                    p.category_id !== expandedCategory &&
                                    p.name.toLowerCase().includes(pickerSearch.toLowerCase())
                                );
                                if (available.length === 0) {
                                    return <p style={{ color: theme?.colors?.textSecondary || '#888', textAlign: 'center', padding: '2rem 0' }}>
                                        {t('noProducts') || 'No products available'}
                                    </p>;
                                }
                                return available.map(product => (
                                    <label
                                        key={product.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.6rem',
                                            padding: '0.6rem 0.75rem',
                                            borderBottom: `1px solid ${theme?.colors?.border || '#eee'}`,
                                            cursor: 'pointer',
                                            backgroundColor: pickerSelectedIds.has(product.id)
                                                ? (theme?.colors?.primary || '#1976d2') + '15'
                                                : 'transparent',
                                            transition: 'background-color 0.1s'
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={pickerSelectedIds.has(product.id)}
                                            onChange={() => {
                                                setPickerSelectedIds(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(product.id)) next.delete(product.id);
                                                    else next.add(product.id);
                                                    return next;
                                                });
                                            }}
                                            style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                                        />
                                        <span style={{ flex: 1, color: theme?.colors?.text || '#000' }}>
                                            {product.name}
                                            {!product.active && <span style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#999', marginLeft: '0.5rem' }}>({t('inactive')})</span>}
                                        </span>
                                        <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary || '#888' }}>
                                            {product.category || t('uncategorized') || 'Uncategorized'}
                                        </span>
                                    </label>
                                ));
                            })()}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => { setShowProductPicker(false); setPickerSearch(''); }}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: theme?.colors?.border || '#ccc',
                                    color: theme?.colors?.text || '#000',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('cancel') || 'Cancel'}
                            </button>
                            <button
                                onClick={() => handleAddProductsToCategory([...pickerSelectedIds])}
                                disabled={pickerSelectedIds.size === 0}
                                style={{
                                    padding: '0.5rem 1rem',
                                    backgroundColor: pickerSelectedIds.size > 0 ? (theme?.colors?.success || '#4caf50') : (theme?.colors?.border || '#ccc'),
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: pickerSelectedIds.size > 0 ? 'pointer' : 'not-allowed',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('addProducts') || 'Add Products'} {pickerSelectedIds.size > 0 && `(${pickerSelectedIds.size})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Add/Edit Modal */}
            {(showAddCategoryForm || editingCategory) && (
                <div
                    onClick={resetCategoryForm}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: theme?.colors?.surface || '#fff',
                            borderRadius: theme?.borderRadius || 8,
                            padding: '2rem',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0 }}>
                            {editingCategory ? t('editCategory') : t('addCategory')}
                        </h3>
                        <form onSubmit={handleCategorySave}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {t('name')}:
                                </label>
                                <input
                                    type="text"
                                    value={categoryFormData.name}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem',
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000'
                                    }}
                                    placeholder="Category name"
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {t('nameSpanish')}:
                                </label>
                                <input
                                    type="text"
                                    value={categoryFormData.name_español}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name_español: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem',
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000'
                                    }}
                                    placeholder="Nombre de categoría"
                                />
                            </div>

                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {t('area')}:
                                </label>
                                <select
                                    value={categoryFormData.area}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, area: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem',
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000'
                                    }}
                                >
                                    <option value="kitchen">Kitchen</option>
                                    <option value="bar">Bar</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: 'bold',
                                    color: theme?.colors?.text || '#000'
                                }}>
                                    {t('displayOrder')}:
                                </label>
                                <input
                                    type="number"
                                    value={getNumberInputValue(categoryFormData.ordinal)}
                                    onChange={(e) => setCategoryFormData({ ...categoryFormData, ordinal: parseNumberInput(e.target.value, { min: 0 }) })}
                                    onWheel={(e) => e.target.blur()}
                                    placeholder="0"
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        borderRadius: theme?.borderRadius || 4,
                                        border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                                        fontSize: '1rem',
                                        backgroundColor: theme?.colors?.surface || '#fff',
                                        color: theme?.colors?.text || '#000'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    onClick={resetCategoryForm}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: theme?.colors?.error || '#f44336',
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: 'pointer',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={!categoryFormData.name}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        backgroundColor: !categoryFormData.name ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                        color: theme?.colors?.surface || '#fff',
                                        border: 'none',
                                        borderRadius: theme?.borderRadius || 4,
                                        cursor: !categoryFormData.name ? 'not-allowed' : 'pointer',
                                        fontWeight: 'bold',
                                        opacity: !categoryFormData.name ? 0.6 : 1
                                    }}
                                >
                                    {t('save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductManagement;